import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { KeyRound, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { api } from "@/lib/api";
import { authErrorMessage, twoFactor } from "@/lib/auth";
import { toast } from "@/components/Toaster";
import { Badge, Button, Card, ErrorNote, Input, Skeleton } from "@/components/ui";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Your own account.
 *
 * There was no screen for this at all. Two-factor sign-in was fully built — the
 * challenge step, backup codes, the shield badge next to members who have it —
 * and nothing anywhere could turn it on, so the challenge was unreachable and
 * the badge could never light. The marketing site advertised it.
 *
 * Password changes go through the existing reset-by-email flow rather than a
 * second in-app path: one way to set a password is easier to keep correct than
 * two, and it works for someone who has forgotten theirs.
 */
export function Account() {
  const { t } = useTranslation(["settings", "common"]);
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });

  return (
    <div className="mx-auto max-w-[620px] space-y-5">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-text">{t("account.title")}</h1>
        <p className="mt-1 text-[13px] text-text-muted">
          {t("account.intro")}
        </p>
      </div>

      <Card className="p-5">
        <h2 className="text-[14px] font-semibold text-text">{t("account.signedInAs")}</h2>
        {me.isLoading ? (
          <Skeleton className="mt-3 h-5 w-56" />
        ) : (
          <div className="mt-2">
            <p className="text-[14px] text-text">{me.data?.name ?? me.data?.email}</p>
            <p className="text-[13px] text-text-muted">{me.data?.email}</p>
            {me.data?.isPlatformOperator ? (
              <Badge tone="accent" className="mt-2">
                <ShieldCheck size={11} /> {t("account.platformOperator")}
              </Badge>
            ) : null}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-[14px] font-semibold text-text">{t("common:language.label")}</h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-text-muted">
          {t("account.languageHelp")}
        </p>
        <div className="mt-3.5">
          <LanguageSwitcher variant="select" />
        </div>
      </Card>

      <TwoFactor />

      <Card className="p-5">
        <h2 className="text-[14px] font-semibold text-text">{t("account.password.title")}</h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-text-muted">
          {t("account.password.body")}
        </p>
        <Button
          variant="secondary"
          className="mt-3.5"
          onClick={() => window.location.assign("/?reset=1")}
        >
          <KeyRound size={14} />
          {t("account.password.sendReset")}
        </Button>
      </Card>
    </div>
  );
}

/**
 * Turn two-factor on, or off.
 *
 * Better Auth returns the TOTP URI and the backup codes exactly once, at
 * enrolment. They are shown here and never fetched again — which is why the
 * copy insists on saving them before closing.
 */
function TwoFactor() {
  const { t } = useTranslation(["settings", "common"]);
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ uri: string; codes: string[] } | null>(null);
  const [mode, setMode] = useState<"idle" | "enabling" | "disabling">("idle");

  async function onEnable(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);

    const { data, error: enableError } = await twoFactor.enable({ password });

    setPending(false);
    setPassword("");

    if (enableError) {
      setError(authErrorMessage(enableError));
      return;
    }

    setSecret({ uri: data!.totpURI, codes: data!.backupCodes });
    setMode("idle");
  }

  async function onDisable(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);

    const { error: disableError } = await twoFactor.disable({ password });

    setPending(false);
    setPassword("");

    if (disableError) {
      setError(authErrorMessage(disableError));
      return;
    }

    setMode("idle");
    toast(t("account.twoFactor.disabled"), "success");
  }

  if (secret) {
    return (
      <Card className="p-5">
        <h2 className="text-[14px] font-semibold text-text">
          {t("account.twoFactor.savedTitle")}
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-text-muted">
          {t("account.twoFactor.savedBody")}
        </p>

        <div className="mt-3.5">
          <span className="mb-1.5 block text-[12.5px] font-medium text-text">
            {t("account.twoFactor.setupKey")}
          </span>
          {/* The raw URI rather than a QR image: rendering a QR would mean
              pulling in a library, and every authenticator app accepts a
              pasted key. */}
          <code className="block overflow-x-auto rounded-lg bg-bg-subtle px-3 py-2 font-mono text-[12px] text-text">
            {secret.uri}
          </code>
        </div>

        <div className="mt-3.5">
          <span className="mb-1.5 block text-[12.5px] font-medium text-text">
            {t("account.twoFactor.backupCodes")}
          </span>
          <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-bg-subtle p-3 sm:grid-cols-3">
            {secret.codes.map((code) => (
              <code key={code} className="font-mono text-[12.5px] text-text">
                {code}
              </code>
            ))}
          </div>
        </div>

        <Button variant="primary" className="mt-4" onClick={() => setSecret(null)}>
          {t("account.twoFactor.savedThem")}
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <h2 className="text-[14px] font-semibold text-text">
        {t("account.twoFactor.title")}
      </h2>
      <p className="mt-1.5 text-[13px] leading-relaxed text-text-muted">
        {t("account.twoFactor.body")}
      </p>

      {mode === "idle" ? (
        <div className="mt-3.5 flex gap-2">
          <Button variant="primary" onClick={() => setMode("enabling")}>
            <ShieldCheck size={14} />
            {t("account.twoFactor.turnOn")}
          </Button>
          <Button variant="ghost" onClick={() => setMode("disabling")}>
            <ShieldOff size={14} />
            {t("account.twoFactor.turnOff")}
          </Button>
        </div>
      ) : (
        <form
          onSubmit={mode === "enabling" ? onEnable : onDisable}
          className="mt-3.5 space-y-3"
        >
          <label className="block">
            <span className="mb-1.5 block text-[12.5px] font-medium text-text">
              {t("account.twoFactor.confirmPassword")}
            </span>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              autoFocus
              className="max-w-[280px]"
            />
          </label>

          {error ? <ErrorNote>{error}</ErrorNote> : null}

          <div className="flex gap-2">
            <Button
              type="submit"
              variant={mode === "enabling" ? "primary" : "danger"}
              disabled={!password || pending}
            >
              {pending ? <Loader2 size={14} className="animate-spin" /> : null}
              {mode === "enabling" ? t("account.twoFactor.enable") : t("account.twoFactor.disable")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setMode("idle");
                setPassword("");
                setError(null);
              }}
            >
              {t("common:actions.cancel")}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
